<template>
  <div class="orders-index-page">
    <div class="d-flex flex-wrap justify-content-between">
      <div>
        <b-button
          variant="outline-danger"
          size="sm"
          class="mb-2"
          @click="clearAllFilters"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Effacer tous les filtres
        </b-button>
        <b-button
          v-if="user.role === 'supplier'"
          variant="success"
          size="sm"
          class="mb-2 ml-2"
          @click="openCsvImportModal"
        >
          <feather-icon
            icon="UploadIcon"
            size="14"
            class="mr-1"
          />
          Importer CSV
        </b-button>
        <b-dropdown
          variant="info"
          size="sm"
          class="mb-2 ml-2"
          text="Exporter"
          right
        >
          <template #button-content>
            <feather-icon
              icon="DownloadIcon"
              size="14"
              class="mr-1"
            />
            Exporter
          </template>
          <b-dropdown-item @click="exportToCSV('selected')">
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            CSV (Sélectionnés)
          </b-dropdown-item>
          <b-dropdown-item @click="exportToCSV('all')">
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            CSV (Tous)
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="exportToPDF('selected')">
            <feather-icon
              icon="FileIcon"
              size="14"
              class="mr-1"
            />
            PDF (Sélectionnés)
          </b-dropdown-item>
          <b-dropdown-item @click="exportToPDF('all')">
            <feather-icon
              icon="FileIcon"
              size="14"
              class="mr-1"
            />
            PDF (Tous)
          </b-dropdown-item>
        </b-dropdown>
        <b-button
          v-if="selectedOrders.length > 0"
          variant="outline-secondary"
          size="sm"
          class="mb-2 ml-2"
          @click="clearSelection"
        >
          <feather-icon
            icon="XCircleIcon"
            size="14"
            class="mr-1"
          />
          Désélectionner ({{ selectedOrders.length }})
        </b-button>
      </div>

      <!-- Bulk Actions for Admin -->
      <div
        v-if="(user.role === 'admin' || user.role === 'superadmin') && selectedOrders.length > 0"
        class="mb-2"
      >
        <b-button
          variant="primary"
          size="sm"
          :disabled="isResyncLoading"
          @click="bulkResynchronizeOrders"
        >
          <b-spinner
            v-if="isResyncLoading"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="RefreshCwIcon"
            size="14"
            class="mr-1"
          />
          Resynchroniser ({{ selectedOrders.length }})
        </b-button>
        <b-button
          v-if="filters.status === '0'"
          variant="danger"
          size="sm"
          :disabled="isBulkDeleteLoading"
          class="ml-2"
          @click="bulkDeleteOrders"
        >
          <b-spinner
            v-if="isBulkDeleteLoading"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="TrashIcon"
            size="14"
            class="mr-1"
          />
          Supprimer ({{ selectedOrders.length }})
        </b-button>
      </div>
    </div>

    <!-- Enhanced Filter Section -->
    <b-card class="mb-3">
      <template #header>
        <h5 class="mb-0">
          Filtres de recherche
        </h5>
      </template>

      <!-- Status Filter -->
      <div class="mb-3 mt-1">
        <label class="mr-2 h6">
          Statut logistique :
          <b-icon-question-circle
            v-b-tooltip.hover
            title="Filtrez les colis selon leur statut logistique professionnel (ex : Livré, Retour, En cours, etc.)"
            class="ml-1 text-info"
          />
        </label>
        <b-form-radio-group
          v-model="filters.status"
          @input="applyFilters"
        >
          <b-form-radio value="">
            Tous les statuts
          </b-form-radio>
          <b-form-radio value="0">
            En attente
          </b-form-radio>
          <b-form-radio value="15">
            En attente de collecte
          </b-form-radio>
          <b-form-radio value="1">
            En cours
          </b-form-radio>
          <b-form-radio value="2">
            Arrivé au dépôt
          </b-form-radio>
          <b-form-radio value="3">
            En cours
          </b-form-radio>
          <b-form-radio value="4">
            Livré au destinataire
          </b-form-radio>
          <b-form-radio value="5">
            Retour au dépôt
          </b-form-radio>
          <b-form-radio value="6">
            Livré et payé (contre remboursement)
          </b-form-radio>
          <b-form-radio value="7">
            Retour définitif expéditeur
          </b-form-radio>
          <b-form-radio value="9">
            Retour payé
          </b-form-radio>
          <b-form-radio value="16">
            À rembourser au fournisseur
          </b-form-radio>
          <b-form-radio value="17">
            Remboursement effectué
          </b-form-radio>
          <b-form-radio value="18">
            Rendez-vous programmé
          </b-form-radio>
          <b-form-radio value="19">
            Livré Temporaire
          </b-form-radio>
          <b-form-radio value="20">
            Retour Temporaire
          </b-form-radio>
        </b-form-radio-group>
      </div>

      <!-- Search Filters Row -->
      <b-row>
        <b-col md="3">
          <b-form-group
            label="Numéro de bon de livraison (BL)"
            label-for="bl"
          >
            <div class="input-with-clear">
              <b-form-input
                id="bl"
                v-model="filters.bl"
                v-debounce:1000="applyFilters"
                placeholder="Saisissez le numéro BL"
                type="text"
              />
              <feather-icon
                v-if="filters.bl"
                icon="XIcon"
                class="clear-input-icon"
                @click="filters.bl = ''; applyFilters()"
              />
            </div>
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Recherchez un colis par son numéro unique de bon de livraison.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Nom du destinataire"
            label-for="name"
          >
            <div class="input-with-clear">
              <b-form-input
                id="name"
                v-model="filters.name"
                v-debounce:1000="applyFilters"
                placeholder="Saisissez le nom du destinataire"
                type="text"
              />
              <feather-icon
                v-if="filters.name"
                icon="XIcon"
                class="clear-input-icon"
                @click="filters.name = ''; applyFilters()"
              />
            </div>
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez par nom du client destinataire du colis.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Téléphone du destinataire"
            label-for="phone"
          >
            <div class="input-with-clear">
              <b-form-input
                id="phone"
                v-model="filters.phone"
                v-debounce:1000="applyFilters"
                placeholder="Saisissez le numéro de téléphone"
                type="text"
              />
              <feather-icon
                v-if="filters.phone"
                icon="XIcon"
                class="clear-input-icon"
                @click="filters.phone = ''; applyFilters()"
              />
            </div>
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Recherchez un colis par numéro de téléphone du destinataire.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Montant TTC min (DT)"
            label-for="minPriceTtc"
          >
            <div class="input-with-clear">
              <b-form-input
                id="minPriceTtc"
                v-model="filters.minPriceTtc"
                v-debounce:1000="applyFilters"
                placeholder="0.000"
                type="number"
                step="0.001"
              />
              <feather-icon
                v-if="filters.minPriceTtc"
                icon="XIcon"
                class="clear-input-icon"
                @click="filters.minPriceTtc = ''; applyFilters()"
              />
            </div>
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez les colis par montant total TTC minimum.
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional Filters Row -->
      <b-row>
        <b-col md="3">
          <b-form-group
            label="Montant TTC max (DT)"
            label-for="maxPriceTtc"
          >
            <div class="input-with-clear">
              <b-form-input
                id="maxPriceTtc"
                v-model="filters.maxPriceTtc"
                v-debounce:1000="applyFilters"
                placeholder="999.999"
                type="number"
                step="0.001"
              />
              <feather-icon
                v-if="filters.maxPriceTtc"
                icon="XIcon"
                class="clear-input-icon"
                @click="filters.maxPriceTtc = ''; applyFilters()"
              />
            </div>
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez les colis par montant total TTC maximum.
            </small>
          </b-form-group>
        </b-col>

        <b-col
          v-if="user.role === 'admin' || user.role === 'superadmin'"
          md="3"
        >
          <b-form-group label="Dépôt logistique">
            <v-select
              v-model="filters.repository"
              :clearable="true"
              placeholder="Sélectionner un dépôt logistique"
              label="name"
              :options="repositories"
              @input="applyFilters"
            />
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez les colis selon le dépôt logistique d'origine ou de destination.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Gouvernorat de destination">
            <v-select
              v-model="filters.governorate"
              :clearable="true"
              placeholder="Sélectionner un gouvernorat de destination"
              label="name"
              :options="governorates"
              @input="applyFilters"
            />
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez par gouvernorat de destination du colis.
            </small>
          </b-form-group>
        </b-col>
        <b-col
          v-if="user.role === 'admin' || user.role === 'superadmin' || user.role === 'manager'"
          md="3"
        >
          <b-form-group label="Fournisseur expéditeur">
            <v-select
              v-model="filters.supplier"
              :clearable="true"
              placeholder="Sélectionner un fournisseur expéditeur"
              label="username"
              :options="filteredSuppliers"
              @input="applyFilters"
            />
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez par fournisseur expéditeur du colis.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Type de colis">
            <v-select
              v-model="filters.isExchange"
              :clearable="true"
              placeholder="Sélectionner le type"
              label="label"
              :options="exchangeOptions"
              @input="applyFilters"
            />
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez par type de colis (Normal ou Échange).
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Fragilité">
            <v-select
              v-model="filters.isFragile"
              :clearable="true"
              placeholder="Sélectionner la fragilité"
              label="label"
              :options="fragileOptions"
              @input="applyFilters"
            />
            <small class="form-text text-muted">
              <b-icon-info-circle class="mr-1" />
              Filtrez par fragilité du colis.
            </small>
          </b-form-group>
        </b-col>
        <b-col
          v-if="user.role === 'supplier'"
          md="3"
          class="d-flex align-items-end"
        >
          <b-button
            :to="{ name: 'order-add' }"
            variant="gradient-primary"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter colis</span>
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading Spinner -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- No Results Found -->
    <div
      v-if="!isLoading && orders.length === 0"
      class="text-center py-5"
    >
      <b-card class="shadow-sm no-results-card">
        <div class="mb-3">
          <feather-icon
            icon="SearchIcon"
            size="48"
            class="text-muted"
          />
        </div>
        <h5 class="text-muted mb-3">
          Aucune commande trouvée
        </h5>
        <p class="text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucune commande ne correspond aux critères de recherche actuels.
            <br>
            Essayez de modifier ou supprimer certains filtres.
          </span>
          <span v-else>
            Il n'y a actuellement aucune commande dans le système.
          </span>
        </p>
        <div v-if="hasActiveFilters">
          <b-button
            variant="outline-primary"
            @click="clearAllFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Réinitialiser les filtres
          </b-button>
        </div>
        <div v-else-if="user.role === 'supplier'">
          <b-button
            :to="{ name: 'order-add' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Créer votre première commande
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Results Table -->
    <vue-good-table
      v-if="!isLoading && orders.length > 0"
      ref="ordersTable"
      :columns="columns"
      :rows="orders"
      :current-page="currentPage"
      :search-options="{ enabled: false }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
      :select-options="selectOptions"
      @on-selected-rows-change="onSelectionChanged"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- BL column with copy button and exchange note/icon -->
        <span
          v-if="props.column.field === 'id'"
          class="text-nowrap d-flex flex-column align-items-start"
        >
          <div class="d-flex align-items-center">
            <span>{{ props.row.id }}</span>
            <b-button
              v-b-tooltip.hover
              title="Copier le BL"
              variant="link"
              size="sm"
              class="ml-1 p-0"
              @click="copyToClipboard(props.row.id, `Le numéro BL ${props.row.id} a été copié !`)"
            >
              <feather-icon
                icon="CopyIcon"
                size="16"
              />
            </b-button>
          </div>
          <div
            v-if="props.row.is_an_exchange"
            class="text-primary mt-1 small font-italic d-flex align-items-center"
          >
            Échange
            <feather-icon
              icon="RefreshCwIcon"
              size="15"
              class="ml-1"
              style="color: #5A8DEE;"
            />
          </div>
          <div
            v-if="props.row.is_fragile"
            class="text-warning mt-1 small font-italic d-flex align-items-center"
          >
            Fragile
            <feather-icon
              icon="AlertTriangleIcon"
              size="15"
              class="ml-1"
              style="color: #FDAC41;"
            />
          </div>
          <div
            v-if="props.row.can_open_package"
            class="mt-1 small font-italic d-flex align-items-center"
            style="color: #C59C6C;"
          >
            Ouverture
            <feather-icon
              icon="PackageIcon"
              size="15"
              class="ml-1"
              style="color: #C59C6C;"
            />
          </div>
        </span>
        <span v-else-if="props.column.field === 'status'">
          <div v-if="props.row.status == 0">
            <b-badge
              class="badge-glow bg-warning bg-lighten-1"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span> En attente</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 15">
            <b-badge
              class="badge-glow bg-info bg-lighten-3"
              pill
            >
              <feather-icon
                icon="ClockIcon"
                class="mr-25"
              />
              <span>En attente de collecte</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 1">
            <b-badge
              class="badge-glow bg-warning bg-darken-2"
              pill
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> En Pickup</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 2">
            <b-badge
              class="badge-glow bg-info bg-lighten-1"
              pill
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> Au dépôt</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 3">
            <b-badge
              class="badge-glow bg-info bg-darken-2"
              pill
              variant="success"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> En cours</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 4">
            <b-badge
              class="badge-glow bg-success bg-lighten-2"
              pill
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> Livré </span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 5">
            <b-badge
              class="badge-glow bg-danger bg-lighten-2"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span> Retour Au dépôt </span>
            </b-badge>
            <div
              v-if="props.row.last_return_reason"
              class="mt-1 small text-danger font-weight-bold"
            >
              <strong>{{ props.row.last_return_reason.return_reason }}</strong>
              <div>
                <div
                  v-if="props.row.last_return_reason.scheduled_date"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="font-italic text-dark">Prévu le :</span>
                  <span class="ml-1">{{ formatDateTime(props.row.last_return_reason.scheduled_date) }}</span>
                </div>
                <div v-if="props.row.last_return_reason.additional_note">
                  <span class="font-italic text-muted">Remarque :</span>
                  <span class="ml-1">{{ props.row.last_return_reason.additional_note }}</span>
                </div>
                <div v-if="props.row.last_return_reason.quick_options && props.row.last_return_reason.quick_options.length">
                  <span class="font-italic text-dark">Options :</span>
                  <span
                    v-for="(opt, idx) in props.row.last_return_reason.quick_options"
                    :key="idx"
                    class="badge badge-pill badge-info ml-1"
                  >
                    {{ opt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="props.row.status == 6">
            <b-badge
              class="badge-glow"
              pill
              variant="success"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> Livré payé </span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 7">
            <b-badge
              class="badge-glow"
              pill
              variant="danger"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                class="mr-25"
              />
              <span>Retour définitif expéditeur</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 8">
            <b-badge
              class="badge-glow bg-secondary bg-lighten-2"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span> Transfert </span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 9">
            <b-badge
              class="badge-glow"
              pill
              variant="danger"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span> Retour payé </span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 10">
            <b-badge
              class="badge-glow"
              pill
              variant="danger"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span>Retour inter-agence</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 11">
            <b-badge
              class="badge-glow"
              pill
              variant="danger"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span>Retour inter-agence reçu</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 14">
            <b-badge
              class="badge-glow"
              pill
              variant="warning"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                class="mr-25"
              />
              <span>À vérifier</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 16">
            <b-badge
              class="badge-glow bg-warning bg-lighten-3"
              pill
            >
              <feather-icon
                icon="DollarSignIcon"
                class="mr-25"
              />
              <span>À rembourser</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 17">
            <b-badge
              class="badge-glow bg-success bg-lighten-3"
              pill
            >
              <feather-icon
                icon="CheckCircleIcon"
                class="mr-25"
              />
              <span>Remboursé</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 12">
            <b-badge
              class="badge-glow bg-danger bg-lighten-2"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span>Colis annulé par admin</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 13">
            <b-badge
              class="badge-glow bg-danger bg-lighten-2"
              pill
            >
              <feather-icon
                icon="AlertTriangleIcon"
                class="mr-25"
              />
              <span>Colis perdu</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 18">
            <b-badge
              class="badge-glow bg-primary bg-lighten-2"
              pill
            >
              <feather-icon
                icon="CalendarIcon"
                class="mr-25"
              />
              <span>Rendez Vous</span>
            </b-badge>
            <div
              v-if="props.row.last_return_reason"
              class="mt-1 small text-primary font-weight-bold"
            >
              <strong>{{ props.row.last_return_reason.return_reason }}</strong>
              <div>
                <div
                  v-if="props.row.last_return_reason.scheduled_date"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="font-italic text-dark">Prévu</span>
                  <span class="ml-1">{{ formatDateTime(props.row.last_return_reason.scheduled_date) }}</span>
                </div>
                <div v-if="props.row.last_return_reason.additional_note">
                  <span class="font-italic text-muted">Remarque :</span>
                  <span class="ml-1">{{ props.row.last_return_reason.additional_note }}</span>
                </div>
                <div v-if="props.row.last_return_reason.quick_options && props.row.last_return_reason.quick_options.length">
                  <span class="font-italic text-dark">Options :</span>
                  <span
                    v-for="(opt, idx) in props.row.last_return_reason.quick_options"
                    :key="idx"
                    class="badge badge-pill badge-info ml-1"
                  >
                    {{ opt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="props.row.status == 19">
            <b-badge
              class="badge-glow bg-success bg-lighten-3"
              pill
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span>Livré Temporaire</span>
            </b-badge>
          </div>
          <div v-if="props.row.status == 20">
            <b-badge
              class="badge-glow bg-warning bg-lighten-3"
              pill
            >
              <feather-icon
                icon="ClockIcon"
                class="mr-25"
              />
              <span>Retour Temporaire</span>
            </b-badge>
            <div
              v-if="props.row.last_return_reason"
              class="mt-1 small text-warning font-weight-bold"
            >
              <strong>{{ props.row.last_return_reason.return_reason }}</strong>
              <div>
                <!-- Show driver name for temporary return reasons -->
                <div
                  v-if="props.row.last_return_reason.is_temporary && props.row.last_return_reason.driver_name"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="font-italic text-dark">Chauffeur :</span>
                  <span class="ml-1">{{ props.row.last_return_reason.driver_name }}</span>
                </div>
                <div
                  v-if="props.row.last_return_reason.scheduled_date"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="font-italic text-dark">Prévu le :</span>
                  <span class="ml-1">{{ formatDateTime(props.row.last_return_reason.scheduled_date) }}</span>
                </div>
                <div v-if="props.row.last_return_reason.additional_note">
                  <span class="font-italic text-muted">Remarque :</span>
                  <span class="ml-1">{{ props.row.last_return_reason.additional_note }}</span>
                </div>
                <!-- Show quick options only for regular return reasons (not temporary) -->
                <div v-if="!props.row.last_return_reason.is_temporary && props.row.last_return_reason.quick_options && props.row.last_return_reason.quick_options.length">
                  <span class="font-italic text-dark">Options :</span>
                  <span
                    v-for="(opt, idx) in props.row.last_return_reason.quick_options"
                    :key="idx"
                    class="badge badge-pill badge-info ml-1"
                  >
                    {{ opt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- Fallback for unknown statuses -->
          <div v-if="![0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].includes(Number(props.row.status))">
            <b-badge
              class="badge-glow"
              pill
              variant="secondary"
            >
              <feather-icon
                icon="HelpCircleIcon"
                class="mr-25"
              />
              <span>Statut inconnu ({{ props.row.status }})</span>
            </b-badge>
          </div>
        </span>
        <span v-else-if="props.column.field === 'phone1'">
          <div>
            <span class="d-inline-flex align-items-center">
              {{ props.row.phone1 }}
              <b-button
                v-b-tooltip.hover
                :title="`Copier téléphone`"
                variant="link"
                size="sm"
                class="p-0 ml-1"
                @click="copyToClipboard(props.row.phone1, `Téléphone ${props.row.phone1} copié !`)"
              >
                <feather-icon
                  icon="CopyIcon"
                  size="16"
                />
              </b-button>
            </span>
            <template v-if="props.row.phone2">
              <br>
              <span class="d-inline-flex align-items-center mt-1">
                {{ props.row.phone2 }}
                <b-button
                  v-b-tooltip.hover
                  :title="`Copier téléphone 2`"
                  variant="link"
                  size="sm"
                  class="p-0 ml-1"
                  @click="copyToClipboard(props.row.phone2, `Téléphone ${props.row.phone2} copié !`)"
                >
                  <feather-icon
                    icon="CopyIcon"
                    size="16"
                  />
                </b-button>
              </span>
            </template>
          </div>
        </span>
        <span v-else-if="props.column.field === 'products'">
          <div>
            <span v-if="props.row.products && props.row.products.length > 0">
              {{ props.row.products[0].product || props.row.products[0].designation || props.row.products[0].name || 'Produit sans nom' }}
              <span
                v-if="props.row.products.length > 1"
                class="badge badge-light ml-1"
              >+{{ props.row.products.length - 1 }}</span>
            </span>
            <span v-else>
              <span class="text-muted">Aucun produit</span>
            </span>
          </div>
        </span>
        <span v-else-if="props.column.field === 'zone'">
          <div>
            <span>
              <!-- Governorate (object or string) -->
              <span v-if="typeof props.row.governorate === 'object' && props.row.governorate.name">
                {{ props.row.governorate.name }}
              </span>
              <span v-else>
                {{ props.row.governorate || '-' }}
              </span>
            </span>
            <br>
            <span class="text-muted small">
              <span v-if="typeof props.row.delegation === 'object' && props.row.delegation.name">
                {{ props.row.delegation.name }}
              </span>
              <span v-else>
                {{ props.row.delegation || '-' }}
              </span>
            </span>
          </div>
        </span>
        <!-- Column: Action -->
        <span v-else-if="props.column.field === 'action'">
          <div class="action-icons-container">
            <!-- Printer Icon -->
            <router-link
              v-if="user.role === 'admin' || user.role === 'superadmin' || user.role === 'manager' || ((props.row.status === 0 || props.row.status === 1) && user.role === 'supplier')"
              class="action-icon text-success"
              :to="{ name: 'delivery-note-show', params: { id: props.row.id } }"
            >
              <feather-icon
                :id="`invoice-row-${props.row.id}-showdel-icon`"
                icon="PrinterIcon"
                size="14"
              />
              <b-tooltip
                title="Bon de livraison"
                :target="`invoice-row-${props.row.id}-showdel-icon`"
              />
            </router-link>

            <!-- Quick View Icon -->
            <span
              :id="`invoice-row-${props.row.id}-quickview-icon`"
              class="action-icon cursor-pointer text-danger"
              @click="openQuickView(props.row.id)"
            >
              <feather-icon
                icon="TargetIcon"
                size="14"
              />
              <b-tooltip
                title="Aperçu rapide"
                :target="`invoice-row-${props.row.id}-quickview-icon`"
              />
            </span>

            <span
              v-if="((user.role ==='admin' || user.role === 'superadmin') && (props.row.status === 0 || props.row.status === 1 || props.row.status === 2 || props.row.status === 3)) || (user.role ==='manager' && (props.row.status === 0 || props.row.status === 1)) || (user.role === 'supplier' && props.row.status === 0 && props.row.supplier && ![164,119,173].includes(props.row.supplier.id))"
              :id="`invoice-row-${props.row.id}-trash-icon`"
              class="action-icon cursor-pointer text-danger"
              @click="deleteOrder(props.row.id)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
              <b-tooltip
                title="Supprimer"
                :target="`invoice-row-${props.row.id}-trash-icon`"
              />
            </span>

            <!-- View Details Icon -->
            <router-link
              class="action-icon text-info"
              :to="{ name: 'order-show', params: { id: props.row.id } }"
            >
              <feather-icon
                :id="`invoice-row-${props.row.id}-show-icon`"
                icon="EyeIcon"
                size="14"
              />
              <b-tooltip
                title="Détail"
                :target="`invoice-row-${props.row.id}-show-icon`"
              />
            </router-link>

            <!-- Edit Icon -->
            <router-link
              v-if="user.role === 'admin' || ((user.role === 'manager' || user.role === 'warehouse_operator') && [0,1,15].includes(props.row.status) && props.row.supplier && props.row.supplier.repository === user.repository)"
              class="action-icon text-warning"
              :to="{ name: 'order-edit', params: { id: props.row.id } }"
            >
              <feather-icon
                :id="`invoice-row-${props.row.id}-edit-icon`"
                icon="EditIcon"
                size="14"
              />
              <b-tooltip
                title="Modifier"
                :target="`invoice-row-${props.row.id}-edit-icon`"
              />
            </router-link>

            <span
              v-if="props.row.return_number >= 1"
              :id="`invoice-row-${props.row.id}-hist-icon`"
              class="action-icon cursor-pointer text-danger"
              @click="openReturnReasonModal(props.row.id)"
            >
              <feather-icon
                icon="InfoIcon"
                size="14"
              />
              <b-tooltip
                title="Historique de retour"
                :target="`invoice-row-${props.row.id}-hist-icon`"
              />
            </span>

            <span
              v-if="props.row.status == 5"
              :id="`invoice-row-${props.row.id}-add-return-reason-icon`"
              class="action-icon cursor-pointer text-success"
              @click="openAddReturnReasonModal(props.row.id)"
            >
              <feather-icon
                icon="PlusCircleIcon"
                size="14"
              />
              <b-tooltip
                title="Ajouter raison de retour"
                :target="`invoice-row-${props.row.id}-add-return-reason-icon`"
              />
            </span>

            <!-- Ticket Section (Full Width) -->
            <template v-if="user.role === 'supplier' || user.role === 'admin' || user.role === 'superadmin'">
              <!-- Quick Create Ticket Icon (Regular Icon) -->
              <span
                v-if="!props.row.today_ticket && props.row.can_create_ticket"
                :id="`invoice-row-${props.row.id}-quickticket-icon`"
                class="action-icon cursor-pointer text-danger"
                @click="openQuickTicketModal(props.row)"
              >
                <feather-icon
                  icon="MessageCircleIcon"
                  size="14"
                />
                <b-tooltip
                  title="Créer un ticket"
                  :target="`invoice-row-${props.row.id}-quickticket-icon`"
                />
              </span>

              <!-- Ticket Status Badge (Full Width) -->
              <span
                v-else-if="props.row.today_ticket"
                :id="`invoice-row-${props.row.id}-ticket-indicator`"
                class="ticket-badge-container"
              >
                <b-badge
                  :variant="getTicketIndicatorColor(props.row.today_ticket)"
                  class="ticket-badge-custom cursor-pointer"
                  :class="{
                    'blinking-badge': props.row.today_ticket.is_default && !props.row.today_ticket.is_closed,
                    'badge-nouveau-ticket': props.row.today_ticket.is_default && !props.row.today_ticket.is_closed,
                    'badge-ticket-ferme': props.row.today_ticket.is_closed,
                    'badge-ticket-en-cours': props.row.today_ticket.status && ((props.row.today_ticket.status.display_name && props.row.today_ticket.status.display_name.toLowerCase().includes('en cours')) || (props.row.today_ticket.status.name && props.row.today_ticket.status.name.toLowerCase().includes('en cours')))
                  }"
                  @click="goToTicketDetail(props.row.today_ticket.id)"
                >
                  <feather-icon
                    icon="MessageCircleIcon"
                    size="12"
                    class="mr-1"
                  />
                  <span v-if="props.row.today_ticket.is_default && !props.row.today_ticket.is_closed">
                    Nouveau
                  </span>
                  <span v-else-if="props.row.today_ticket.status && ((props.row.today_ticket.status.display_name && props.row.today_ticket.status.display_name.toLowerCase().includes('en cours')) || (props.row.today_ticket.status.name && props.row.today_ticket.status.name.toLowerCase().includes('en cours')))">
                    En cours
                  </span>
                  <span v-else-if="props.row.today_ticket.is_closed">
                    Clôturé
                  </span>
                  <span v-else-if="props.row.today_ticket.status && (props.row.today_ticket.status.display_name || props.row.today_ticket.status.name)">
                    {{ props.row.today_ticket.status.display_name || props.row.today_ticket.status.name }}
                  </span>
                  <span v-else>
                    Ticket
                  </span>
                </b-badge>
                <b-tooltip
                  :target="`invoice-row-${props.row.id}-ticket-indicator`"
                  :title="getTicketIndicatorTooltip(props.row.today_ticket)"
                />
              </span>
            </template>
          </div>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Enhanced Pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap align-items-center">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap mr-2">
              Affichage {{ ((currentPage - 1) * pageLength) + 1 }} à {{ Math.min(currentPage * pageLength, count) }}
            </span>
            <b-form-select
              v-model="pageLength"
              :options="[
                { value: 50, text: '50' },
                { value: 100, text: '100' },
                { value: 500, text: '500' }
              ]"
              class="mx-1"
              size="sm"
              style="width: 80px;"
              @input="(value) => {
                props.perPageChanged({currentPerPage:value});
                applyFilters();
              }"
            />
            <span class="text-nowrap ml-2"> de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              size="sm"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Summary Card for Admin/Manager Users -->
    <b-card
      v-if="!isLoading && orders.length > 0 && (user.role === 'admin' || user.role === 'superadmin' || user.role === 'manager')"
      class="mt-3"
      header="Résumé des Commandes"
    >
      <b-row>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-primary">
              {{ count }}
            </h4>
            <p class="text-muted">
              Total Commandes
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-success">
              {{ deliveredOrdersCount }}
            </h4>
            <p class="text-muted">
              Commandes Livrées
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-warning">
              {{ pendingOrdersCount }}
            </h4>
            <p class="text-muted">
              En Attente
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-info">
              {{ totalRevenue }} DT
            </h4>
            <p class="text-muted">
              Chiffre d'Affaires
            </p>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Quick View Component -->
    <order-quick-view
      :visible="showQuickView"
      :order-id="selectedOrderId"
      @close="closeQuickView"
    />

    <!-- Ticket Quick View Drawer -->
    <ticket-quick-view
      v-if="showTicketQuickView && selectedTicketId"
      :value="showTicketQuickView"
      :ticket-id="selectedTicketId"
      @input="showTicketQuickView = $event"
      @close="closeTicketQuickView"
    />

    <!-- Return Reason Modal -->
    <b-modal
      ref="returnReasonModal"
      title="Historique de retour"
    >
      <ul
        v-for="reason in returnDetails"
        :key="reason.content"
      >
        <li>{{ reason.motif_retour }} - {{ reason.created_at }}</li>
      </ul>
    </b-modal>

    <!-- Add Return Reason Modal -->
    <add-return-reason-modal
      v-if="selectedOrderForReturnReason !== null"
      ref="addReturnReasonModal"
      :order-id="selectedOrderForReturnReason"
      :order-bl="selectedOrderBlForReturnReason"
      @reason-added="onReturnReasonAdded"
    />

    <!-- Quick Create Ticket Modal -->
    <b-modal
      v-model="showQuickTicketModal"
      title="Créer un ticket pour la commande"
      hide-footer
      size="md"
      centered
      :no-close-on-backdrop="quickTicketLoading"
      :no-close-on-esc="quickTicketLoading"
      class="modern-modal"
    >
      <!-- Loading overlay after submit -->
      <transition name="fade">
        <div
          v-if="quickTicketLoadingOverlay"
          class="quick-ticket-loading-overlay d-flex flex-column align-items-center justify-content-center"
        >
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem;"
          />
          <div
            class="mt-3 font-weight-bold text-primary"
            style="font-size: 1.1rem;"
          >
            Création du ticket...
          </div>
        </div>
      </transition>
      <b-form @submit.prevent="submitQuickTicket">
        <b-form-group
          label="Catégorie *"
          label-for="quick-ticket-category"
        >
          <b-form-select
            id="quick-ticket-category"
            v-model="quickTicketForm.category"
            :options="quickTicketCategories.map(c => ({ value: c.id, text: c.display_name }))"
            required
            placeholder="Choisir une catégorie"
            :disabled="quickTicketLoading"
          />
        </b-form-group>
        <b-form-group
          label="Description (optionnel)"
          label-for="quick-ticket-desc"
        >
          <vue-editor
            id="quick-ticket-desc"
            v-model="quickTicketForm.description"
            placeholder="Décrivez le problème... (optionnel)"
            :editor-toolbar="[
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link', 'image'],
              ['clean'],
            ]"
            style="min-height: 120px; background: #f8f9fa; border-radius: 0.75rem; border: 1.5px solid #e80000;"
            :disabled="quickTicketLoading"
          />
        </b-form-group>
        <div class="d-flex align-items-center justify-content-between">
          <b-button
            type="submit"
            variant="primary"
            class="modern-btn"
            :disabled="quickTicketLoading"
          >
            <b-spinner
              v-if="quickTicketLoading"
              small
              class="mr-1"
            />
            <span v-if="!quickTicketLoading">Créer le ticket</span>
            <span v-else>Création...</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="quickTicketLoading"
            @click="showQuickTicketModal = false"
          >
            Annuler
          </b-button>
        </div>
        <div
          v-if="quickTicketError"
          class="text-danger mt-2"
        >
          {{ quickTicketError }}
        </div>
        <div
          v-if="quickTicketSuccess"
          class="text-success mt-2"
        >
          Ticket créé avec succès !
        </div>
      </b-form>
    </b-modal>

    <!-- CSV Import Modal -->
    <csv-import-modal
      ref="csvImportModal"
      @imported="onCsvImported"
    />
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormInput, BButton, BTooltip, BBadge,
  BFormRadio, BFormRadioGroup, BSpinner, BFormSelect, BRow, BCol,
  BModal, BCard, BForm, BDropdown, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import formatDistance from 'date-fns/formatDistance'
import { fr } from 'date-fns/locale'
import AddReturnReasonModal from '@/components/modals/AddReturnReasonModal.vue'
import TicketQuickView from '@/views/support/tickets/TicketQuickView.vue'
import { VueEditor } from 'vue2-editor'
import CsvImportModal from '@/components/orders/CsvImportModal.vue'
import OrderQuickView from './QuickView.vue'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BTooltip,
    BButton,
    BBadge,
    BFormRadioGroup,
    BFormRadio,
    vSelect,
    BSpinner,
    BFormSelect,
    BRow,
    BCol,
    BModal,
    BCard,
    BForm,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    OrderQuickView,
    AddReturnReasonModal,
    TicketQuickView,
    VueEditor,
    CsvImportModal,
  },
  props: {
    supplier: {
      type: String,
      default: () => null,
    },
    level: {
      type: Number,
      default: () => 0,
    },
  },
  data() {
    return {
      size: 48, // was 80
      date: null,
      status: 0,
      pageLength: 100,
      rangeDate: null,
      dir: false,
      currentPage: 1,
      count: 0,
      name: '',
      phone: '',
      governorates: [],
      filteredSuppliers: [],
      allSuppliers: [],
      isFilteringSuppliers: false,
      governorate: '',
      columns: [
        {
          label: 'Action',
          field: 'action',
          width: '120px',
          sortable: false,
        },
        {
          label: 'BL',
          field: 'id',
          width: '130px',
        },
        {
          label: 'Client',
          field: 'name',
        },
        {
          field: 'last_return_reason',
          label: 'Dernière raison de retour',
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          sortable: false,
          width: '220px',
        },
      ],

      // Removed 'Gouvernorat' and 'Délégation' columns
      repository: { id: null },
      repositories: [],
      role: '',
      orders: [],
      user: {},
      isLoading: false,
      bl: '',
      awaitingSearch: false,
      supplier1: {
        id: null,
      },
      returnDetails: {},
      showPopup: false,

      // Quick View Properties
      showQuickView: false,
      selectedOrderId: null,

      // Ticket Quick View Drawer
      showTicketQuickView: false,
      selectedTicketId: null,

      // Return Reason Modal Properties
      selectedOrderForReturnReason: null,
      selectedOrderBlForReturnReason: '',

      // Quick Create Ticket Modal
      showQuickTicketModal: false,
      quickTicketOrder: null,
      quickTicketForm: {
        category: null,
        description: '',
      },
      quickTicketLoading: false,
      quickTicketLoadingOverlay: false,
      quickTicketCategories: [],
      quickTicketError: '',
      quickTicketSuccess: false,

      filters: {
        status: '',
        bl: '',
        name: '',
        phone: '',
        governorate: null,
        supplier: null,
        repository: null,
        minPriceTtc: '',
        maxPriceTtc: '',
        // Newly added reactive filters
        isExchange: null,
        isFragile: null,
      },

      // Applied filters for display
      // Options for filters
      exchangeOptions: [
        { value: true, label: 'Échange' },
        { value: false, label: 'Normal' },
      ],
      fragileOptions: [
        { value: true, label: 'Fragile' },
        { value: false, label: 'Non fragile' },
      ],

      appliedFilters: [],

      // Bulk selection and resync functionality
      selectedOrders: [],
      isResyncLoading: false,
      isBulkDeleteLoading: false,
      selectionObserver: null, // For watching selection bar changes
      selectOptions: {
        enabled: true, // Enable selection for all users including suppliers
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-selection-info-styled',
        selectionText: 'commandes sélectionnées',
        clearSelectionText: 'Désélectionner tout',
        disableSelectInfo: false, // Enable default selection info
        selectAllByGroup: true,
        clearSelectionClass: 'btn btn-sm btn-outline-light ml-2',
      },

      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1, // Monday
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return this.appliedFilters.length > 0
    },

    /**
     * Calculate delivered orders count
     */
    deliveredOrdersCount() {
      return this.orders.filter(order => order.status === 4 || order.status === 6).length
    },

    /**
     * Calculate pending orders count
     */
    pendingOrdersCount() {
      return this.orders.filter(order => order.status === 0).length
    },

    /**
     * Calculate total revenue from delivered orders
     */
    totalRevenue() {
      return this.orders
        .filter(order => order.status === 4 || order.status === 6)
        .reduce((sum, order) => sum + parseFloat(order.price_ttc || 0), 0)
        .toFixed(3)
    },
  },

  watch: {
    // Watch for filter status changes and update suppliers
    'filters.status': {
      handler() {
        // Only filter suppliers for admin/superadmin/manager roles and if suppliers are loaded
        if ((this.user.role === 'admin' || this.user.role === 'superadmin' || this.user.role === 'manager') && this.allSuppliers && this.allSuppliers.length > 0) {
          this.filterSuppliers()
        }
      },
    },
    $route(to, from) {
      // Only update filters if the route has actually changed
      if (to.params.level !== from.params.level) {
        this.initializeFiltersFromRoute()
      }
    },
  },

  created() {
    this.user = storeAuth.state.user

    // Enable selection only for admin and superadmin users
    if (this.user.role === 'admin' || this.user.role === 'superadmin') {
      this.selectOptions.enabled = true
    }

    // Dynamic column configuration based on user role
    this.setupColumns()

    // Get query parameters if provided
    const {
      governorate, delegation, locality, ...otherFilters
    } = this.$route.query

    // If we have query parameters, set the filters accordingly
    if (governorate) { this.filters.governorate = governorate }
    if (delegation) { this.filters.delegation = delegation }
    if (locality) { this.filters.locality = locality }

    // Set other filters from query
    Object.keys(otherFilters).forEach(key => {
      this.filters[key] = otherFilters[key]
    })

    // If a specific level is provided in the route, use that
    if (this.level) {
      this.status = Number(this.level)
    } else {
      // Set status to null for "Tous les colis"
      this.status = 0
    }

    // Initialize filters based on route parameters
    this.initializeFiltersFromRoute()

    // Always call getOrders to load data
    this.getOrders(1)
    if (this.user.role === 'admin' || this.user.role === 'superadmin') {
      this.getRepositories()
      this.getAllSuppliers()
    }
    if (this.user.role === 'manager') {
      this.getAllSuppliers()
    }
    this.getGovernorates()
  },

  mounted() {
    // Simplified setup - just ensure the table clears selections properly
    this.$nextTick(() => {
      this.setupSimpleDeselection()
    })
  },

  beforeDestroy() {
    // Clean up if needed
  },

  methods: {
    /**
     * Open CSV import modal
     */
    openCsvImportModal() {
      this.$refs.csvImportModal.openModal()
    },
    /**
     * Handle CSV import completion
     */
    onCsvImported(result) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Import Réussi',
          text: `${result.created_count} colis importés avec succès${result.failed_count > 0 ? `. ${result.failed_count} ont échoué.` : ''}`,
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
      // Refresh the orders list
      this.getOrders()
    },
    /**
     * Export orders to CSV
     */
    exportToCSV(type) {
      let ordersToExport = []

      if (type === 'selected') {
        if (this.selectedOrders.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Aucune sélection',
              text: 'Veuillez sélectionner au moins un colis à exporter',
              variant: 'warning',
              icon: 'AlertCircleIcon',
            },
          })
          return
        }
        ordersToExport = this.selectedOrders
      } else {
        ordersToExport = this.orders
      }

      // CSV headers
      const headers = [
        'Numéro',
        'Code-barres',
        'Client',
        'Téléphone',
        'Adresse',
        'Gouvernorat',
        'Délégation',
        'Localité',
        'Prix TTC (DT)',
        'Produits',
        'Statut',
        'Date de création',
        'Note',
      ]

      // Convert orders to CSV rows
      const rows = ordersToExport.map(order => [
        order.id,
        order.barcode || '',
        order.name,
        order.phone1,
        order.address,
        order.governorate || '',
        order.delegation || '',
        '', // locality is not available in the API
        order.price_ttc,
        this.formatProducts(order.products),
        this.getStatusLabel(order.status),
        new Date(order.created_at).toLocaleDateString('fr-FR'),
        order.note || '',
      ])

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
      ].join('\n')

      // Create blob and download
      const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `orders_${type}_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Export CSV',
          text: `${ordersToExport.length} colis exportés avec succès`,
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
    },
    /**
     * Export orders to PDF using HTML print
     */
    exportToPDF(type) {
      let ordersToExport = []

      if (type === 'selected') {
        if (this.selectedOrders.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Aucune sélection',
              text: 'Veuillez sélectionner au moins un colis à exporter',
              variant: 'warning',
              icon: 'AlertCircleIcon',
            },
          })
          return
        }
        ordersToExport = this.selectedOrders
      } else {
        ordersToExport = this.orders
      }

      // Create HTML for PDF export
      const printWindow = window.open('', '_blank')
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Liste des Colis - ${new Date().toLocaleDateString('fr-FR')}</title>
          <style>
            @media print {
              @page { margin: 1cm; }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              color: #7367f0;
              margin-bottom: 10px;
            }
            .info {
              margin-bottom: 20px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th {
              background-color: #7367f0;
              color: white;
              padding: 10px;
              text-align: left;
              font-size: 12px;
            }
            td {
              padding: 8px;
              border-bottom: 1px solid #ddd;
              font-size: 11px;
            }
            tr:hover {
              background-color: #f5f5f5;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #999;
              font-size: 10px;
            }
          </style>
        </head>
        <body>
          <h1>Liste des Colis</h1>
          <div class="info">
            <strong>Date d'export:</strong> ${new Date().toLocaleString('fr-FR')}<br>
            <strong>Total:</strong> ${ordersToExport.length} colis
          </div>
          <table>
            <thead>
              <tr>
                <th>N° Colis</th>
                <th>Code-barres</th>
                <th>Client</th>
                <th>Téléphone</th>
                <th>Gouvernorat</th>
                <th>Délégation</th>
                <th>Prix TTC</th>
                <th>Statut</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${ordersToExport.map(order => `${'<tr>'
                  + '<td>'}${order.id}</td>`
                  + `<td>${order.barcode || '-'}</td>`
                  + `<td>${order.name}</td>`
                  + `<td>${order.phone1}</td>`
                  + `<td>${order.governorate || '-'}</td>`
                  + `<td>${order.delegation || '-'}</td>`
                  + `<td>${order.price_ttc} DT</td>`
                  + `<td>${this.getStatusLabel(order.status)}</td>`
                  + `<td>${new Date(order.created_at).toLocaleDateString('fr-FR')}</td>`
                + '</tr>').join('')}
            </tbody>
          </table>
          <div class="footer">
            Document généré automatiquement - ${new Date().toLocaleString('fr-FR')}
          </div>
        </body>
        </html>
      `

      // Write HTML first
      printWindow.document.write(html)
      printWindow.document.close()

      // Add print script separately to avoid ESLint issues
      const script = printWindow.document.createElement('script')
      script.textContent = 'window.onload = function() { window.print(); }'
      printWindow.document.body.appendChild(script)

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Export PDF',
          text: 'Fenêtre d\'impression ouverte. Utilisez "Enregistrer au format PDF" dans les options d\'impression.',
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
    },
    /**
     * Format products array for display
     */
    formatProducts(products) {
      if (!products || products.length === 0) return ''
      return products.map(p => `${p.product || p.name || ''} (x${p.quantity || 1})`).join(', ')
    },
    /**
     * Setup simple deselection handling
     */
    setupSimpleDeselection() {
      // Add a global click listener to handle the "Désélectionner tout" button
      document.addEventListener('click', event => {
        const { target } = event

        console.log('Click detected on:', target.tagName, target.className, target.textContent)

        // Check if the clicked element is the deselection button
        if (target && (
          target.classList.contains('vgt-clear-selection')
          || target.classList.contains('custom-deselect-btn')
          || (target.textContent && (
            target.textContent.includes('Désélectionner')
            || target.textContent.includes('Clear')
            || target.textContent.includes('tout')
          ))
        )) {
          console.log('Deselection button clicked - preventing default and calling clearSelection')
          event.preventDefault()
          event.stopPropagation()
          this.clearSelection()
        }
      })
    },
    /**
     * Robust copy to clipboard with fallback and debug log
     */
    async copyToClipboard(value, message = null) {
      console.log('copyToClipboard called with:', value)
      let success = false
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(String(value))
          success = true
        } catch (e) {
          success = false
        }
      }
      if (!success) {
        try {
          const textarea = document.createElement('textarea')
          textarea.value = String(value)
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'absolute'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
          success = true
        } catch (e) {
          success = false
        }
      }
      if (success) {
        this.showToast(
          'success',
          'top-center',
          message || `Valeur copiée: ${value}`,
        )
      } else {
        this.showToast(
          'danger',
          'top-center',
          'Erreur lors de la copie.',
        )
      }
    },
    getReturnNumberColor(returnNumber) {
      if (returnNumber === 0) {
        return 'secondary' // Gray color for 0
      } if (returnNumber === 1) {
        return 'info' // Blue color for 1
      } if (returnNumber === 2) {
        return 'warning' // Yellow color for 2
      } if (returnNumber >= 3) {
        return 'danger' // Red color for 3 or more
      }
      return 'secondary' // Default color
    },
    async openReturnReasonModal(orderId) {
      const { data } = await axios.get(`/api/orders/${orderId}/return-details/`)
      this.returnDetails = data
      this.$refs.returnReasonModal.show()
    },
    clearGovernorate() {
      this.governorate = ''
      // Always use getOrders to preserve all other filters
      this.getOrders(1)
    },
    clearSupplier() {
      this.supplier1 = { id: null }
      // Always use getOrders to preserve all other filters
      this.getOrders(1)
    },
    clearDepot() {
      this.repository = {}
      this.getOrders(1)
    },
    async getGovernorates() {
      const { data } = await axios.get('/api/governorates/')
      this.governorates = data
    },
    async getAllSuppliers() {
      try {
        const { data } = await axios.get('api/auth/users/suppliers/')
        this.allSuppliers = Array.isArray(data) ? data : []

        // Always show all suppliers initially
        this.filteredSuppliers = this.allSuppliers

        // Filter suppliers based on current filter status
        if (this.filters.status === '0') {
          await this.filterSuppliers()
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        this.showToast('danger', 'top-center', 'Error fetching suppliers')
      }
    },
    async filterSuppliers() {
      if (this.isFilteringSuppliers) {
        return
      }

      this.isFilteringSuppliers = true

      try {
        if (!this.allSuppliers || this.allSuppliers.length === 0) {
          this.filteredSuppliers = []
          return
        }

        if (this.filters.status === '0') {
          const { data } = await axios.get('/api/auth/users/suppliers-with-pending-orders/', {
            params: {
              with_pending_orders: true,
            },
          })
          this.filteredSuppliers = data
        } else {
          this.filteredSuppliers = this.allSuppliers
        }
      } catch (error) {
        console.error('Error filtering suppliers:', error)
        this.showToast('danger', 'top-center', 'Error filtering suppliers')
        // Fallback to all suppliers on error
        this.filteredSuppliers = this.allSuppliers || []
      } finally {
        this.isFilteringSuppliers = false
      }
    },
    async findByGovernorate(currentPage) {
      this.isLoading = true
      this.pageLength = 100
      // Don't reset text search filters - preserve them
      try {
        const params = {
          governorate: this.governorate.id,
          status: this.status,
          repository: this.repository.id,
          p: currentPage,
          supplier: this.supplier1.id,
          page_size: this.pageLength,
          // Preserve text search filters
          name: this.name,
          bl: this.bl,
          phone: this.phone,
        }
        const { data } = await axios.get('/api/orders/list/', { params })
        this.orders = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error searching by governorate')
      } finally {
        this.isLoading = false
      }
    },
    async findBySupplier(currentPage) {
      this.isLoading = true
      this.pageLength = 100
      // Don't reset text search filters - preserve them
      try {
        const params = {
          supplier: this.supplier1.id,
          status: this.status,
          repository: this.repository.id,
          governorate: this.governorate.id,
          p: currentPage,
          page_size: this.pageLength,
          // Preserve text search filters
          name: this.name,
          bl: this.bl,
          phone: this.phone,
        }
        const { data } = await axios.get('/api/orders/list/', { params })
        this.orders = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error searching by supplier')
      } finally {
        this.isLoading = false
      }
    },
    async getRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    formatFn(value) {
      return formatDistance(new Date(value), new Date(), { addSuffix: true, locale: fr })
    },
    async getSuppliers() {
      const { data } = await axios.get('api/auth/users/suppliers/')
      this.suppliers = data
    },

    // Updated unified search methods that work with the new filter system
    async findById() {
      // Convert to filter-based search
      this.filters.bl = this.bl
      this.bl = '' // Clear legacy field
      await this.applyFilters()
    },

    async findByName() {
      // Convert to filter-based search
      this.filters.name = this.name
      this.name = '' // Clear legacy field
      await this.applyFilters()
    },

    async findByPhone() {
      // Convert to filter-based search
      this.filters.phone = this.phone
      this.phone = '' // Clear legacy field
      await this.applyFilters()
    },

    async loadGovernorateById(id) {
      try {
        const { data } = await axios.get(`/api/governorates/${id}/`)
        this.governorate = data
        this.findByGovernorate(1)
      } catch (error) {
        console.error('Error loading governorate:', error)
      }
    },
    async loadDelegationById(id) {
      try {
        const { data } = await axios.get(`/api/delegations/${id}/`)
        this.loadGovernorateById(data.governorate.id)
        setTimeout(() => {
          this.findByDelegation(id, 1)
        }, 500)
      } catch (error) {
        console.error('Error loading delegation:', error)
      }
    },
    async loadLocalityById(id) {
      try {
        const { data } = await axios.get(`/api/localities/${id}/`)
        this.loadDelegationById(data.delegation.id)
        setTimeout(() => {
          this.findByLocality(id, 1)
        }, 1000)
      } catch (error) {
        console.error('Error loading locality:', error)
      }
    },
    async findByDelegation(delegationId, currentPage) {
      this.isLoading = true
      this.pageLength = 100
      // Don't reset text search filters - preserve them

      const params = {
        status: this.status,
        repository: this.repository.id,
        p: currentPage,
        supplier: this.supplier1.id,
        // Preserve text search filters
        name: this.name,
        bl: this.bl,
        phone: this.phone,
      }

      try {
        const { data } = await axios.get(`/api/orders/search-by-delegation/${delegationId}`, { params })
        this.orders = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error searching by delegation')
      } finally {
        this.isLoading = false
      }
    },
    async findByLocality(localityId, currentPage) {
      this.isLoading = true
      this.pageLength = 100
      // Don't reset text search filters - preserve them

      const params = {
        status: this.status,
        repository: this.repository.id,
        p: currentPage,
        supplier: this.supplier1.id,
        // Preserve text search filters
        name: this.name,
        bl: this.bl,
        phone: this.phone,
      }

      try {
        const { data } = await axios.get(`/api/orders/search-by-locality/${localityId}`, { params })
        this.orders = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error searching by locality')
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update the route with the current filter state as query parameters
     */
    async updateRouteFromStatus() {
      // Build query object from current filters, excluding status
      const query = {}
      if (this.filters && typeof this.filters === 'object') {
        Object.keys(this.filters).forEach(key => {
          if (key !== 'status' && this.filters[key] !== null && this.filters[key] !== '' && this.filters[key] !== undefined) {
            // Serialize select objects to primitives for URL query
            if (key === 'governorate' && this.filters[key] && typeof this.filters[key] === 'object' && 'id' in this.filters[key]) {
              query.governorate = this.filters[key].id
            } else if (key === 'supplier' && this.filters[key] && typeof this.filters[key] === 'object' && 'id' in this.filters[key]) {
              query.supplier = this.filters[key].id
            } else if (key === 'isExchange') {
              const v = typeof this.filters[key] === 'object' ? this.filters[key].value : this.filters[key]
              query.isExchange = v
            } else if (key === 'isFragile') {
              const v = typeof this.filters[key] === 'object' ? this.filters[key].value : this.filters[key]
              query.isFragile = v
            } else {
              query[key] = this.filters[key]
            }
          }
        })
      }
      // If status is set, use it as path param (level), else go to /orders
      if (this.filters.status !== '' && this.filters.status !== null && this.filters.status !== undefined) {
        this.$router.push({
          name: 'order-list',
          params: { level: this.filters.status },
          query,
        })
      } else {
        this.$router.push({
          name: 'orders-index',
          query,
        })
      }
    },

    /**
     * Apply filters and update the route
     */
    async applyFilters() {
      await this.fetchOrders()
      this.updateRouteFromStatus()
    },

    /**
     * Fetch orders with current filters - ENHANCED VERSION
     */
    async fetchOrders() {
      this.isLoading = true

      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageLength,
        }

        Object.keys(this.filters).forEach(key => {
          const value = this.filters[key]
          if (value !== null && value !== '' && value !== false) {
            if (key === 'governorate' && value && typeof value === 'object') {
              params.governorate = value.id
            } else if (key === 'supplier' && value && typeof value === 'object') {
              params.supplier = value.id
            } else if (key === 'isExchange') {
              // normalize to boolean and snake_case for backend
              const v = typeof value === 'object' && value !== null && 'value' in value ? value.value : value
              if (typeof v === 'boolean') params.is_exchange = v
            } else if (key === 'isFragile') {
              const v = typeof value === 'object' && value !== null && 'value' in value ? value.value : value
              if (typeof v === 'boolean') params.is_fragile = v
            } else if (key === 'repository' && value && typeof value === 'object') {
              params.repository = value.id
            } else if (key === 'minPriceTtc') {
              params.price_ttc_min = value
            } else if (key === 'maxPriceTtc') {
              params.price_ttc_max = value
            } else {
              params[key] = value
            }
          }
        })

        const { data } = await axios.get('/api/orders/list/', { params })

        this.orders = data.results || []
        this.count = data.count || 0
        this.totalPages = data.total_pages || Math.ceil(this.count / this.pageLength)
      } catch (error) {
        console.error('Error fetching orders:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des commandes')
        this.orders = []
        this.count = 0
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Handle pagination - SIMPLIFIED VERSION
     */
    async pageNext(currentPage) {
      this.currentPage = currentPage
      await this.fetchOrders()
    },

    /**
     * Unified getOrders method - SIMPLIFIED VERSION
     */
    async getOrders(currentPage) {
      this.currentPage = currentPage || 1
      await this.fetchOrders()
    },

    /**
     * Update the applied filters display - ENHANCED VERSION
     */
    updateAppliedFilters() {
      this.appliedFilters = []

      // Status filter
      if (this.filters.status !== null && this.filters.status !== '') {
        const statusLabels = {
          0: 'En attente',
          1: 'En pickup',
          2: 'Au dépôt',
          3: 'En cours',
          4: 'Livré',
          5: 'Retour au dépôt',
          6: 'Livré payé',
          7: 'Retour définitif expéditeur',
          8: 'Transfert',
          9: 'Retour payé',
          10: 'Retour inter-agence',
          11: 'Retour inter-agence reçu',
          12: 'Colis annulé par admin',
          13: 'Colis perdu',
          14: 'À vérifier',
          15: 'En attente de collecte',
          16: 'À rembourser',
          17: 'Remboursé',
          18: 'Rendez Vous',
        }
        this.appliedFilters.push({
          key: 'status',
          label: 'Statut',
          value: statusLabels[this.filters.status] || `Statut ${this.filters.status}`,
        })
      }

      // Text filters
      const textFilters = {
        bl: 'Numéro BL',
        name: 'Nom du client',
        phone: 'Téléphone',
      }

      Object.keys(textFilters).forEach(key => {
        if (this.filters[key]) {
          this.appliedFilters.push({
            key,
            label: textFilters[key],
            value: this.filters[key],
          })
        }
      })

      // Price filters
      if (this.filters.minPriceTtc) {
        this.appliedFilters.push({
          key: 'minPriceTtc',
          label: 'Prix TTC min.',
          value: `${this.filters.minPriceTtc} DT`,
        })
      }

      if (this.filters.maxPriceTtc) {
        this.appliedFilters.push({
          key: 'maxPriceTtc',
          label: 'Prix TTC max.',
          value: `${this.filters.maxPriceTtc} DT`,
        })
      }

      // ...existing code...

      // Select filters
      if (this.filters.governorate && this.filters.governorate.name) {
        this.appliedFilters.push({
          key: 'governorate',
          label: 'Gouvernorat',
          value: this.filters.governorate.name,
        })
      }

      if (this.filters.supplier && this.filters.supplier.username) {
        this.appliedFilters.push({
          key: 'supplier',
          label: 'Fournisseur',
          value: this.filters.supplier.username,
        })
      }

      if (this.filters.repository && this.filters.repository.name) {
        this.appliedFilters.push({
          key: 'repository',
          label: 'Dépôt',
          value: this.filters.repository.name,
        })
      }
    },

    /**
     * Remove a specific filter
     */
    removeFilter(filterKey) {
      if (filterKey === 'governorate' || filterKey === 'supplier' || filterKey === 'repository') {
        this.filters[filterKey] = null
      } else if (filterKey === 'is_exchange' || filterKey === 'has_return') {
        this.filters[filterKey] = false
      } else if (filterKey === 'minPriceTtc' || filterKey === 'maxPriceTtc') {
        this.filters[filterKey] = ''
      } else {
        this.filters[filterKey] = filterKey === 'status' ? null : ''
      }
      this.applyFilters()
    },

    /**
     * Clear all filters
     */
    clearAllFilters() {
      // Reset all filters to their initial state
      this.filters = {
        status: '',
        bl: '',
        name: '',
        phone: '',
        governorate: null,
        supplier: null,
        repository: null,
        minPriceTtc: '',
        maxPriceTtc: '',
        isExchange: null,
        isFragile: null,
        centralHubReceivedAt: null,
        centralHubReceivedAtStart: null,
        centralHubReceivedAtEnd: null,
        centralHubDateRangeMode: false,
      }

      // Reset legacy filter fields (for AllOrders.vue compatibility)
      this.bl = ''
      this.name = ''
      this.phone = ''
      this.governorate = ''
      this.supplier1 = { id: null }
      this.repository = { id: null }
      this.isExchange = null
      this.isFragile = null

      // Reset suppliers to show all
      if (this.allSuppliers && this.allSuppliers.length > 0) {
        this.filteredSuppliers = this.allSuppliers
      }

      // Reset current page
      this.currentPage = 1

      // Apply filters to fetch fresh data
      this.applyFilters()

      // Navigate to all orders route without level parameter
      if (this.$route.params.level !== undefined) {
        this.$router.push({ name: 'all-orders' }).catch(err => {
          // Ignore navigation duplicated errors
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('fr-FR')
    },

    /**
     * Format date for API (ensure YYYY-MM-DD format)
     */
    formatDateForAPI(date) {
      if (!date) return ''
      // Bootstrap Vue datepicker returns date in YYYY-MM-DD format
      return date
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },

    async deleteOrder(id) {
      try {
        const result = await this.$swal({
          title: 'Vous êtes sûr?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'oui je veux supprimer ! ',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })
        this.isLoading = true
        if (result.value) {
          try {
            await axios.delete(`/api/orders/delete/${id}/`)
            this.isLoading = false
            await this.getOrders()
            setTimeout(() => {
              this.showToast('success', 'top-center', 'colis supprimé avec succés')
            }, 1000)
          } catch (error) {
            this.isLoading = false
            setTimeout(() => {
              this.showToast('danger', 'top-center', error.toString())
            }, 1000)
          }
        } else if (result.dismiss === 'cancel') {
          this.isLoading = false
          await this.$swal({
            title: 'Fermer',
            text: 'colis Non Supprimée :)',
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
        }
      } catch (e) {
        // handle error if needed
      }
    },
    initializeFiltersFromRoute() {
      // Get status from route params
      const routeStatus = this.$route.params.level

      if (routeStatus !== undefined && routeStatus !== null) {
        // Convert to string to match radio button values
        this.filters.status = String(routeStatus)
      } else {
        // If no level parameter, reset to show all statuses
        this.filters.status = ''
      }

      // Normalize boolean select filters (isExchange, isFragile) from route query
      const normalizeBooleanToOption = (raw, options) => {
        if (raw === null || raw === undefined || !Array.isArray(options)) return null
        // If already an option object, return as-is
        if (typeof raw === 'object' && raw !== null && 'value' in raw) return raw
        // Accept boolean, 'true'/'false', '1'/'0'
        let boolVal = null
        if (typeof raw === 'boolean') boolVal = raw
        else if (typeof raw === 'string') {
          const lower = raw.toLowerCase()
          if (lower === 'true' || lower === '1') boolVal = true
          else if (lower === 'false' || lower === '0') boolVal = false
        } else if (typeof raw === 'number') {
          boolVal = raw === 1
        }
        if (boolVal === null) return null
        return options.find(opt => opt && typeof opt === 'object' && 'value' in opt && opt.value === boolVal) || null
      }

      const q = this.$route.query || {}
      if ('isExchange' in q) {
        this.filters.isExchange = normalizeBooleanToOption(q.isExchange, this.exchangeOptions)
      }
      if ('isFragile' in q) {
        this.filters.isFragile = normalizeBooleanToOption(q.isFragile, this.fragileOptions)
      }

      // Apply filters after setting the status
      this.$nextTick(() => {
        this.applyFilters()
      })
    },

    /**
     * Setup columns based on user role
     */
    setupColumns() {
      // Base columns
      const baseColumns = [
        {
          label: 'Action',
          field: 'action',
          width: '90px',
        },
        {
          label: 'BL',
          field: 'id',
          width: '130px',
        },
        {
          label: 'Client',
          field: 'name',
        },
      ]

      // Conditionally add supplier column (only for non-supplier users)
      if (this.user.role !== 'supplier') {
        baseColumns.push({
          label: 'Fournisseur',
          field: 'supplier.username',
        })
      }

      // Add remaining columns
      const remainingColumns = [
        {
          label: 'Zone / Adresse',
          field: 'zone',
        },
        {
          label: 'Téléphone',
          field: 'phone1',
        },
        {
          label: 'Produit',
          field: 'products',
          width: '150px',
        },
        {
          label: 'Etat',
          field: 'status',
          width: '110px',
        },
        {
          label: 'Prix TTC',
          field: 'price_ttc',
          width: '110px',
        },
        {
          label: 'N°retour',
          field: 'return_number',
          width: '110px',
        },
        {
          label: 'Date de Livraison',
          formatFn: value => (value ? this.formatFn(value) : '-'),
          field: 'delivered_at',
          width: '130px',
        },
      ]

      // Add central_hub_received_at column only for admin, superadmin and manager users (not suppliers)
      if (this.user.role === 'admin' || this.user.role === 'superadmin' || this.user.role === 'manager') {
        remainingColumns.push({
          label: 'Date Réception',
          formatFn: value => (value ? this.formatFn(value) : '-'),
          field: 'central_hub_received_at',
          width: '130px',
        })
      }

      // Combine all columns
      this.columns = [...baseColumns, ...remainingColumns]

      // Add "Synchronisé par" column only if user is admin or superadmin
      if (this.user.role === 'admin' || this.user.role === 'superadmin') {
        this.columns.push({
          label: 'Synchronisé avec',
          field: 'outsourcing_company',
          width: '130px',
        })
      }
    },

    /**
     * Quick View Methods
     */
    async openQuickView(orderId) {
      this.showQuickView = true
      this.selectedOrderId = orderId
    },

    closeQuickView() {
      this.showQuickView = false
      this.selectedOrderId = null
    },

    /**
     * Open the add return reason modal for a specific order
     */
    openAddReturnReasonModal(orderId) {
      const order = this.orders.find(o => o.id === orderId)
      this.selectedOrderForReturnReason = orderId
      this.selectedOrderBlForReturnReason = order && order.id ? order.id : ''
      this.$nextTick(() => {
        this.$refs.addReturnReasonModal.show()
      })
    },

    /**
     * Handle the return reason added event
     */
    onReturnReasonAdded() {
      // Refresh the orders list to show updated return reason count
      this.fetchOrders()

      // Show success message
      this.showToast(
        'success',
        'bottom-right',
        'La raison de retour a été ajoutée avec succès',
      )
    },

    /**
     * Open the quick create ticket modal
     */
    async openQuickTicketModal(order) {
      this.quickTicketOrder = order
      this.quickTicketForm = { category: null, description: '' }
      this.quickTicketError = ''
      this.quickTicketSuccess = false
      this.showQuickTicketModal = true
      await this.fetchQuickTicketCategories()
    },
    async fetchQuickTicketCategories() {
      try {
        const { data } = await axios.get('/api/support/categories/')
        this.quickTicketCategories = data
      } catch (e) {
        this.quickTicketCategories = []
      }
    },
    async submitQuickTicket() {
      this.quickTicketLoading = true
      this.quickTicketLoadingOverlay = false
      this.quickTicketError = ''
      try {
        // Show overlay after form submit, before opening quick view
        const overlayDelay = setTimeout(() => {
          this.quickTicketLoadingOverlay = true
        }, 400) // show overlay if backend is slow

        const { data } = await axios.post('/api/support/tickets/', {
          category_id: this.quickTicketForm.category,
          description: this.quickTicketForm.description,
          order_ids: [this.quickTicketOrder.id],
        })
        clearTimeout(overlayDelay)
        this.quickTicketSuccess = true
        const idx = this.orders.findIndex(o => o.id === this.quickTicketOrder.id)
        if (idx !== -1) {
          this.orders[idx].today_ticket = { ...data, is_default: true }
        }
        // Show overlay while waiting for quick view to open
        this.quickTicketLoadingOverlay = true
        setTimeout(() => {
          this.showQuickTicketModal = false
          this.quickTicketLoadingOverlay = false
          if (data && data.id) {
            this.openTicketQuickView(data.id)
          }
        }, 1200)
      } catch (e) {
        this.quickTicketError = e.response?.data?.detail || 'Erreur lors de la création du ticket.'
        this.quickTicketLoadingOverlay = false
      } finally {
        this.quickTicketLoading = false
      }
    },

    /**
     * Get status variant for badges
     */
    getStatusVariant(status) {
      const variants = {
        0: 'warning',
        1: 'info',
        2: 'primary',
        3: 'secondary',
        4: 'success',
        5: 'danger',
        6: 'success',
        7: 'danger',
        8: 'info',
        9: 'danger',
        10: 'danger',
        11: 'danger',
        12: 'danger',
        13: 'danger',
        14: 'warning',
        15: 'info',
      }
      return variants[status] || 'secondary'
    },

    /**
     * Get status icon
     */
    getStatusIcon(status) {
      const icons = {
        0: 'ClockIcon',
        1: 'TruckIcon',
        2: 'HomeIcon',
        3: 'NavigationIcon',
        4: 'CheckCircleIcon',
        5: 'XCircleIcon',
        6: 'CheckCircle2Icon',
        7: 'CornerUpLeftIcon',
        8: 'ArrowRightIcon',
        9: 'CornerUpLeftIcon',
        10: 'ArrowLeftIcon',
        11: 'ArrowDownIcon',
        12: 'XIcon',
        13: 'AlertTriangleIcon',
        14: 'AlertTriangleIcon',
        15: 'ClockIcon',
      }
      return icons[status] || 'HelpCircleIcon'
    },

    /**
     * Get status label
     */
    getStatusLabel(status) {
      const labels = {
        0: 'En attente',
        1: 'En pickup',
        2: 'Au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour définitif expéditeur',
        8: 'Transfert',
        9: 'Retour payé',
        10: 'Retour inter-agence',
        11: 'Retour inter-agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'À rembourser',
        17: 'Remboursé',
        18: 'Rendez Vous',
      }
      return labels[status] || `Statut inconnu (${status})`
    },

    /**
     * Format date for display
     */
    formatDateTime(dateString) {
      if (!dateString) return '-'

      try {
        const date = new Date(dateString)
        return date.toLocaleString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (error) {
        return dateString
      }
    },
    getTicketIndicatorColor(ticket) {
      if (ticket.is_closed) return 'success'
      if (ticket.is_default) return 'info'
      // Check if ticket status is "en cours" - show blue color
      if (
        ticket.status
        && ((ticket.status.display_name && ticket.status.display_name.toLowerCase().includes('en cours'))
         || (ticket.status.name && ticket.status.name.toLowerCase().includes('en cours')))
      ) {
        return 'primary'
      }
      return 'secondary'
    },
    getTicketIndicatorTooltip(ticket) {
      if (!ticket) return ''
      // New ticket (created today, not closed)
      if (ticket.is_default && !ticket.is_closed) {
        return "L'équipe support va bientôt commencer le traitement de ce ticket."
      }
      // In progress (status is 'En cours' or similar)
      if (
        ticket.status
        && ((ticket.status.display_name && ticket.status.display_name.toLowerCase().includes('en cours'))
         || (ticket.status.name && ticket.status.name.toLowerCase().includes('en cours')))
      ) {
        return 'Réclamation en cours de traitement.'
      }
      // Closed ticket
      if (ticket.is_closed && ticket.closed_at) {
        return `Ticket clôturé depuis ${this.formatFn(ticket.closed_at)}`
      }
      // Default: created today
      return "Ticket créé aujourd'hui"
    },
    goToTicketDetail(ticketId) {
      // this.$router.push({ name: 'support-ticket-detail', params: { id: ticketId } })
      this.openTicketQuickView(ticketId)
    },
    openTicketQuickView(ticketId) {
      this.selectedTicketId = ticketId
      this.showTicketQuickView = true
    },
    closeTicketQuickView() {
      this.showTicketQuickView = false
      this.selectedTicketId = null
    },

    /**
     * Handle selection changes in the table
     */
    onSelectionChanged(params) {
      this.selectedOrders = params.selectedRows || []
      console.log('Selection changed:', this.selectedOrders.length, 'items selected')
    },

    /**
     * Alternative clear selection method - for testing
     */
    forceClearSelection() {
      // Reset local state
      this.selectedOrders = []

      // Uncheck all checkboxes in the table
      this.$nextTick(() => {
        const allCheckboxes = document.querySelectorAll('.vgt-table input[type="checkbox"]')
        allCheckboxes.forEach(checkbox => {
          if (checkbox.checked) {
            checkbox.click()
          }
        })

        // Trigger the selection change event
        this.onSelectionChanged({ selectedRows: [] })

        // Force update
        this.$forceUpdate()
      })
    },
    /**
     * Clear all selections
     */
    clearSelection() {
      this.forceClearSelection()
    },

    /**
     * Bulk resynchronize selected orders
     */
    async bulkResynchronizeOrders() {
      if (this.selectedOrders.length === 0) {
        this.showToast('warning', 'top-center', 'Aucune commande sélectionnée')
        return
      }

      // Confirm action
      const confirmed = await this.$swal({
        title: 'Confirmer la resynchronisation',
        text: `Êtes-vous sûr de vouloir resynchroniser ${this.selectedOrders.length} commande(s) ?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, resynchroniser',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#007bff',
        cancelButtonColor: '#6c757d',
      })

      if (!confirmed.isConfirmed) {
        return
      }

      this.isResyncLoading = true

      try {
        const orderIds = this.selectedOrders.map(order => order.id.toString())

        const response = await axios.post('/api/orders/resynchronize-bulk/', {
          order_ids: orderIds,
        })

        const { results } = response.data

        // Count results
        const queued = results.queued?.length || 0
        const skipped = results.skipped_already_synchronized?.length || 0
        const noOutsourcing = results.no_active_outsourcing?.length || 0
        const notFound = results.not_found?.length || 0
        const failed = results.failed?.length || 0

        // Build detailed message with order IDs
        const messageLines = []

        if (queued > 0) {
          const queuedIds = results.queued.map(item => item.order_id).join(', ')
          messageLines.push(`✅ ${queued} commande(s) resynchronisée(s): ${queuedIds}`)
        }

        if (skipped > 0) {
          const skippedIds = results.skipped_already_synchronized.map(item => item.order_id).join(', ')
          messageLines.push(`⏭️ ${skipped} déjà synchronisée(s): ${skippedIds}`)
        }

        if (noOutsourcing > 0) {
          const noOutsourcingIds = results.no_active_outsourcing.map(item => item.order_id).join(', ')
          messageLines.push(`⚠️ ${noOutsourcing} sans partenariat actif: ${noOutsourcingIds}`)
        }

        if (failed > 0) {
          const failedIds = results.failed.map(item => item.order_id).join(', ')
          messageLines.push(`❌ ${failed} en erreur: ${failedIds}`)
        }

        if (notFound > 0) {
          const notFoundIds = results.not_found.map(item => item.order_id).join(', ')
          messageLines.push(`🔍 ${notFound} non trouvée(s): ${notFoundIds}`)
        }

        // Show detailed results in a popup
        const result = await this.$swal({
          title: 'Résultat de la resynchronisation',
          html: messageLines.join('<br>'),
          icon: queued > 0 ? 'success' : 'info',
          confirmButtonText: 'OK',
          width: '600px',
        })

        // Reload page immediately when user clicks OK
        if (result.isConfirmed) {
          window.location.reload()
        }

        // Simple toast message
        const totalProcessed = queued + skipped + noOutsourcing + failed + notFound
        const successMessage = `Resynchronisation terminée: ${totalProcessed} commande(s) traitée(s)`
        this.showToast('success', 'top-center', successMessage)

        // Clear selection after successful operation
        this.clearSelection()
      } catch (error) {
        console.error('Resync error:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la resynchronisation'
        this.showToast('danger', 'top-center', `Erreur: ${errorMessage}`)
      } finally {
        this.isResyncLoading = false
      }
    },

    /**
     * Bulk delete selected orders (only for status 0 - en attente)
     */
    async bulkDeleteOrders() {
      if (this.selectedOrders.length === 0) {
        this.showToast('warning', 'top-center', 'Aucune commande sélectionnée')
        return
      }

      // Confirm action
      const confirmed = await this.$swal({
        title: 'Confirmer la suppression',
        html: `<p>Êtes-vous sûr de vouloir supprimer <strong>${this.selectedOrders.length} commande(s)</strong> ?</p><p class="text-danger"><strong>Cette action est irréversible !</strong></p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      })

      if (!confirmed.isConfirmed) {
        return
      }

      this.isBulkDeleteLoading = true

      try {
        // Extract order IDs
        const orderIds = this.selectedOrders.map(order => order.id)

        // Call bulk delete API
        const response = await axios.post('/api/orders/bulk-delete/', {
          order_ids: orderIds,
        })

        // Show success message
        const deletedCount = response.data.deleted_count
        const supplierCreatedCount = response.data.supplier_created_count || 0

        let successMessage = `${deletedCount} commande(s) supprimée(s) avec succès`
        if (supplierCreatedCount > 0) {
          successMessage += ` (${supplierCreatedCount} fiche(s) client créée(s))`
        }

        this.showToast('success', 'top-center', successMessage)

        // Refresh orders list
        await this.getOrders()

        // Clear selection
        this.clearSelection()
      } catch (error) {
        console.error('Bulk delete error:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la suppression'

        // Check if there are invalid orders
        if (error.response?.data?.invalid_order_ids) {
          const invalidIds = error.response.data.invalid_order_ids.join(', ')
          this.showToast('danger', 'top-center', `${errorMessage}\nCommandes invalides: ${invalidIds}`)
        } else {
          this.showToast('danger', 'top-center', `Erreur: ${errorMessage}`)
        }
      } finally {
        this.isBulkDeleteLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
// Remove scoped and use component-specific selectors
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

.orders-index-page {
  .applied-filters {
    .badge {
      font-size: 0.875rem;
      padding: 0.5rem 0.75rem;
    }
  }

  // Beautiful selection info styling
  .custom-selection-info-styled {
    background: linear-gradient(135deg, #e30613 0%, #c2050f 100%) !important;
    color: white !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 12px 20px !important;
    margin: 10px 0 15px 0 !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    box-shadow: 0 2px 8px rgba(227, 6, 19, 0.3) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    position: relative !important;

    .vgt-selection-info-text {
      color: white !important;
      font-weight: 600 !important;
    }

    // Style our custom deselection button
    .custom-deselect-btn {
      background: rgba(255, 255, 255, 0.2) !important;
      color: white !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      border-radius: 6px !important;
      padding: 6px 12px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      margin-left: auto !important;

      &:hover {
        background: rgba(255, 255, 255, 0.3) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        transform: translateY(-1px) !important;
      }

      &:active {
        transform: translateY(0) !important;
      }
    }

    .vgt-clear-selection {
      background: rgba(255, 255, 255, 0.2) !important;
      color: white !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      border-radius: 6px !important;
      padding: 6px 12px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      transition: all 0.2s ease !important;
      cursor: pointer !important;
      display: inline-block !important;
      visibility: visible !important;

      &:hover {
        background: rgba(255, 255, 255, 0.3) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        transform: translateY(-1px) !important;
      }

      &:active {
        transform: translateY(0) !important;
      }
    }

    // Force show clear selection link/button
    .vgt-selection-info .vgt-clear-selection,
    .vgt-selection-info a {
      background: rgba(255, 255, 255, 0.2) !important;
      color: white !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      border-radius: 6px !important;
      padding: 6px 12px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      text-decoration: none !important;
      transition: all 0.2s ease !important;
      cursor: pointer !important;
      display: inline-block !important;

      &:hover {
        background: rgba(255, 255, 255, 0.3) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        color: white !important;
        text-decoration: none !important;
        transform: translateY(-1px) !important;
      }
    }
  }

  // Red selection theme (matching project brand colors) - More specific selectors
  .vue-good-table {
    // Force red checkboxes with higher specificity
    .vgt-table .vgt-checkbox input[type="checkbox"] {
      accent-color: #e90d1c !important;
      width: 16px;
      height: 16px;
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      appearance: none !important;
      background-color: white !important;
      border: 2px solid #e30613 !important;
      border-radius: 3px !important;
      position: relative !important;

      &:checked {
        background-color: #e30613 !important;
        border-color: #e30613 !important;
        color: white !important;

        // Add checkmark
        &::after {
          content: '✓' !important;
          position: absolute !important;
          top: -2px !important;
          left: 1px !important;
          color: white !important;
          font-size: 12px !important;
          font-weight: bold !important;
        }
      }

      &:focus {
        outline: 2px solid rgba(227, 6, 19, 0.3) !important;
        outline-offset: 2px !important;
      }

      &:hover {
        border-color: #c2050f !important;
      }
    }

    // Header checkbox with more specificity
    .vgt-table .vgt-table-header .vgt-checkbox input[type="checkbox"] {
      accent-color: #e30613 !important;
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      appearance: none !important;
      background-color: white !important;
      border: 2px solid #e30613 !important;
      border-radius: 3px !important;
      width: 16px !important;
      height: 16px !important;
      position: relative !important;

      &:checked {
        background-color: #e30613 !important;
        border-color: #e30613 !important;
        color: white !important;

        // Add checkmark
        &::after {
          content: '✓' !important;
          position: absolute !important;
          top: -2px !important;
          left: 1px !important;
          color: white !important;
          font-size: 12px !important;
          font-weight: bold !important;
        }
      }

      &:focus {
        outline: 2px solid rgba(227, 6, 19, 0.3) !important;
        outline-offset: 2px !important;
      }

      &:hover {
        border-color: #c2050f !important;
      }
    }

    // Selected row highlighting
    .vgt-table .vgt-row-selected {
      background-color: rgba(227, 6, 19, 0.08) !important;
      border-left: 3px solid #e30613 !important;

      &:hover {
        background-color: rgba(227, 6, 19, 0.12) !important;
      }
    }

    // Show and style the selection info (remove the hiding rules)
    .vgt-selection-info-row {
      display: table-row !important;
      visibility: visible !important;
      height: auto !important;
      overflow: visible !important;
    }

    .vgt-inner-wrap .vgt-selection-info,
    .vgt-selection-info {
      display: block !important;
      visibility: visible !important;
      height: auto !important;
      overflow: visible !important;
    }
  }

  // Additional fallback for checkbox styling - More comprehensive
  input[type="checkbox"] {
    &.vgt-checkbox-col {
      accent-color: #e30613 !important;
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      appearance: none !important;
      background-color: white !important;
      border: 2px solid #e30613 !important;
      border-radius: 3px !important;
      width: 16px !important;
      height: 16px !important;
      position: relative !important;

      &:checked {
        background-color: #e30613 !important;
        border-color: #e30613 !important;

        // Add checkmark
        &::after {
          content: '✓' !important;
          position: absolute !important;
          top: -2px !important;
          left: 1px !important;
          color: white !important;
          font-size: 12px !important;
          font-weight: bold !important;
        }
      }

      &:focus {
        outline: 2px solid rgba(227, 6, 19, 0.3) !important;
        outline-offset: 2px !important;
      }

      &:hover {
        border-color: #c2050f !important;
      }
    }
  }

  // Global override for any missed checkboxes
  .orders-index-page input[type="checkbox"] {
    accent-color: #e30613 !important;

    &:not(.vgt-checkbox-col) {
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      appearance: none !important;
      background-color: white !important;
      border: 2px solid #e30613 !important;
      border-radius: 3px !important;
      width: 16px !important;
      height: 16px !important;
      position: relative !important;

      &:checked {
        background-color: #e30613 !important;
        border-color: #e30613 !important;

        &::after {
          content: '✓' !important;
          position: absolute !important;
          top: -2px !important;
          left: 1px !important;
          color: white !important;
          font-size: 12px !important;
          font-weight: bold !important;
        }
      }
    }
  }  // Enhanced styling for no results card
  .no-results-card {
    border: 2px dashed #e3e6f0;
    background-color: #f8f9fc;

    .feather-icon {
      opacity: 0.5;
    }
  }

  // Table styling improvements
  .vue-good-table {
    .font-weight-semibold {
      font-weight: 600;
    }

    .text-right {
      text-align: right;
    }

    .text-center {
      text-align: center;
    }

    .vgt-table th,
    .vgt-table td {
      padding-left: 0.5rem !important;
      padding-right:  0.5rem !important;
    }
    .vgt-table th:first-child,
    .vgt-table td:first-child {
      padding-left: 0.25rem !important;
    }
  }

  // --- Ticket badge customizations ---
  // Remove all blue backgrounds for ticket badges
  .ticket-badge-custom {
    background-color: #e3e6f0 !important; // light gray for default
    color: #1d1d1b !important;
    border: none !important;
    font-weight: 600;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    margin-left: 0.25rem !important;
  }
  // "Nouveau ticket" (brand red)
  .badge-nouveau-ticket {
    background-color: #e30613 !important;
    color: #fff !important;
    border: none !important;
  }
  // "Ticket fermé" (brand dark)
  .badge-ticket-ferme {
    background-color: #1d1d1b !important;
    color: #fff !important;
    border: none !important;
  }
  // "En cours" ticket (blue)
  .badge-ticket-en-cours {
    background: rgba(91, 166, 227, 0.68) !important;
    color: #fff !important;
    border: none !important;
  }

  // Remove any info/blue badge backgrounds from vue-good-table
  .badge-info,
  .bg-info,
  .badge.bg-info,
  .badge.bg-info.bg-lighten-3 {
    background-color: #c2050f !important;
    color: #1d1d1b !important;
    border: none !important;
  }

  // Blinking effect for new ticket
  .blinking-badge {
    animation: blink-badge 1s linear infinite;
  }
  @keyframes blink-badge {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  // Quick ticket loading overlay style
  .quick-ticket-loading-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.85);
    z-index: 10;
    border-radius: 0.75rem;
    min-height: 320px;
    transition: opacity 0.2s;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  // Action icons container styles - organized grid layout
  .action-icons-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 4px 0;
    justify-items: center;
    align-items: center;
    width: 100%;
  }

  .ticket-badge-container {
    grid-column: 1 / -1;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2px;

    .ticket-badge-custom {
      font-size: 10px !important;
      padding: 2px 6px !important;
      line-height: 1.2;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .action-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .action-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    transition: all 0.15s ease;

    &:hover {
      transform: scale(1.15);
      opacity: 0.85;
    }
  }

  .action-icon-placeholder {
    display: inline-block;
    width: 24px;
    height: 24px;
  }
}
</style>
